import React, { useState } from "react";
import { Instagram, Youtube, ArrowRight, Loader2 } from "lucide-react";
import axios from "axios";
import { useI18n } from "../i18n/I18nContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={18} height={18} {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.42a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.85z" />
  </svg>
);

export default function Footer() {
  const { t, locale } = useI18n();
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ loading: false, ok: false });

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState({ loading: true, ok: false });
    try {
      await axios.post(`${API}/newsletter`, { email, source: "footer", locale });
      setState({ loading: false, ok: true });
      setEmail("");
    } catch {
      setState({ loading: false, ok: false });
    }
  };

  return (
    <footer className="relative pt-20 pb-10 border-t border-white/10 bg-black" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="font-display text-4xl sm:text-5xl tracking-tight">
              MASTER<span style={{ color: "var(--mf-orange)" }}>FIT</span>
            </div>
            <p className="mt-4 text-zinc-400 max-w-md">{t.footer.tagline}</p>

            <div className="mt-10">
              <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-4">
                {t.footer.newsletter}
              </div>
              <form onSubmit={submit} className="flex border-b border-white/20 focus-within:border-[#FF6B00] transition" data-testid="newsletter-form">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.footer.newsletterPlaceholder}
                  className="flex-1 bg-transparent py-3 text-white outline-none placeholder:text-zinc-600"
                  data-testid="newsletter-email"
                />
                <button
                  type="submit"
                  className="px-4 text-[#FF6B00] hover:text-white transition flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-bold"
                  disabled={state.loading}
                  data-testid="newsletter-submit"
                >
                  {state.loading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={16} />}
                  {t.footer.newsletterCta}
                </button>
              </form>
              {state.ok && <div className="mt-3 text-xs text-green-400" data-testid="newsletter-success">✓ {t.footer.newsletterSuccess}</div>}
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-4">{t.footer.links}</div>
            <ul className="space-y-3 text-sm">
              {["about", "coaching", "results", "content", "blog", "contact"].map((k) => (
                <li key={k}>
                  <a
                    href={`#${k === "coaching" ? "pricing" : k}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(k === "coaching" ? "pricing" : k)?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="text-zinc-400 hover:text-[#FF6B00] transition uppercase tracking-wider"
                  >
                    {t.nav[k]}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-4">{t.footer.legal}</div>
            <ul className="space-y-3 text-sm">
              <li><a href="#" className="text-zinc-400 hover:text-[#FF6B00] transition">{t.footer.privacy}</a></li>
              <li><a href="#" className="text-zinc-400 hover:text-[#FF6B00] transition">{t.footer.terms}</a></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-4">{t.footer.follow}</div>
            <div className="flex gap-4">
              <a href="https://www.instagram.com/jorgemasterfit/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/15 flex items-center justify-center hover:border-[#FF6B00] hover:text-[#FF6B00] transition" data-testid="footer-instagram"><Instagram size={16} /></a>
              <a href="https://www.tiktok.com/@jorgemasterfit" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/15 flex items-center justify-center hover:border-[#FF6B00] hover:text-[#FF6B00] transition" data-testid="footer-tiktok"><TikTokIcon /></a>
              <a href="https://www.youtube.com/@jorgemasterfit" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white/15 flex items-center justify-center hover:border-[#FF6B00] hover:text-[#FF6B00] transition" data-testid="footer-youtube"><Youtube size={16} /></a>
            </div>
          </div>
        </div>

        <div className="mf-divider mt-16" />
        <div className="mt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-zinc-600">
          <div>© {new Date().getFullYear()} Masterfit · Jorge Aguilar Valenzuela. {t.footer.rights}</div>
          <div className="tracking-[0.3em] uppercase">PDC · MX · WORLD</div>
        </div>
      </div>
    </footer>
  );
}
